import { z } from 'zod'
import { AgentProvenanceEntrySchema } from './agent_provenance_entry.ts'
import { CompanyTeamEntrySchema } from './company_team_entry.ts'
import { TaskRecordSchema } from './task_record.ts'
import { UserEntrySchema } from './user_entry.ts'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { ProspectsModelFields } from './prospects_model.ts'
import { PipelineProspectsModelFields } from './pipeline_prospects_model.ts'
import { AccountsModelFields } from './accounts_model.ts'
import { PipelineStagesModelFields } from './pipeline_stages_model.ts'
import { PipelineConfigModelFields } from './pipeline_config_model.ts'
import { FundsModelFields } from './funds_model.ts'
import {
  TABLE_PROSPECTS,
  TABLE_ACCOUNTS,
  TABLE_PIPELINE_PROSPECTS,
  TABLE_PIPELINE_CONFIG,
  TABLE_PIPELINE_STAGES,
  TABLE_FUNDS,
  TABLE_CONTACT_ACCOUNTS,
  TABLE_CONTACT_PROSPECTS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_LABEL
} from '../globals.ts'

const ALIAS_PROSPECTS = `${TABLE_PROSPECTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_PROSPECTS = `${TABLE_PIPELINE_PROSPECTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_CONFIG = `${TABLE_PIPELINE_CONFIG}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_ACCOUNTS = `${TABLE_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_STAGES = `${TABLE_PIPELINE_STAGES}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_FUNDS = `${TABLE_FUNDS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_PROSPECTS = `${TABLE_CONTACT_PROSPECTS}${FIELD_KEY_SEPARATOR}` as const

const PROG_COMPANY_TEAM = `${FIELD_KEY_PROG}company_team` as const
const PROG_TASKS = `${FIELD_KEY_PROG}tasks` as const
const PROG_ACCOUNT_INTERACTION_DATES = `${FIELD_KEY_PROG}account_interaction_dates` as const
const FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY = `${ALIAS_CONTACT_ACCOUNTS}introduced_by` as const
const FIELD_CONTACT_ACCOUNTS_SOURCED_BY = `${ALIAS_CONTACT_ACCOUNTS}sourced_by` as const
const FIELD_CONTACT_PROSPECTS_LEAD = `${ALIAS_CONTACT_PROSPECTS}lead` as const

const PROG_IS_NEW_INVESTOR = `${FIELD_KEY_PROG}is_new_investor` as const
const FIELD_PROSPECT_ID = `${ALIAS_PROSPECTS}${ProspectsModelFields.id}` as const
const FIELD_PROSPECT_INVESTOR = `${ALIAS_PROSPECTS}${ProspectsModelFields.investor}` as const
const FIELD_PROSPECT_FUND = `${ALIAS_PROSPECTS}${ProspectsModelFields.fund}` as const
const FIELD_PROSPECT_CURRENCY = `${ALIAS_PROSPECTS}${ProspectsModelFields.currency}` as const
const FIELD_PROSPECT_COMMITMENT = `${ALIAS_PROSPECTS}${ProspectsModelFields.commitment}` as const
const FIELD_PROSPECT_ALLOCATION_AVAILABLE = `${ALIAS_PROSPECTS}${ProspectsModelFields.allocation_available}` as const
const FIELD_PROSPECT_PIPELINE_ORIGINATION_ID = `${ALIAS_PROSPECTS}${ProspectsModelFields.pipeline_origination}` as const
const FIELD_PROSPECT_PIPELINE_EXECUTION_ID = `${ALIAS_PROSPECTS}${ProspectsModelFields.pipeline_execution}` as const
const FIELD_PROSPECT_NOTES = `${ALIAS_PROSPECTS}${ProspectsModelFields.notes}` as const

const FIELD_PIPELINE_PROSPECTS_STAGE = `${ALIAS_PIPELINE_PROSPECTS}${PipelineProspectsModelFields.stage}` as const
const FIELD_PIPELINE_PROSPECTS_STAGE_DATE = `${ALIAS_PIPELINE_PROSPECTS}${PipelineProspectsModelFields.stage_date}` as const
const FIELD_PIPELINE_PROSPECTS_SEQUENCE = `${ALIAS_PIPELINE_PROSPECTS}${PipelineProspectsModelFields.sequence}` as const
const FIELD_PIPELINE_PROSPECTS_ID = `${ALIAS_PIPELINE_PROSPECTS}${PipelineProspectsModelFields.id}` as const
const FIELD_PIPELINE_CONFIG_ID = `${ALIAS_PIPELINE_CONFIG}${PipelineConfigModelFields.id}` as const

const FIELD_ACC_ID = `${ALIAS_ACCOUNTS}${AccountsModelFields.id}` as const
const FIELD_ACC_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.name}` as const
const FIELD_ACC_DESCRIPTION = `${ALIAS_ACCOUNTS}${AccountsModelFields.description}` as const
const FIELD_ACC_NOTES = `${ALIAS_ACCOUNTS}${AccountsModelFields.notes}` as const
const FIELD_ACC_BUSINESS_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_city_code}` as const
const FIELD_ACC_BUSINESS_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_country_code}` as const
const FIELD_ACC_BUSINESS_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_stateprovince}` as const
const FIELD_ACC_BUSINESS_ADDRESS_1 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_1}` as const
const FIELD_ACC_BUSINESS_ADDRESS_2 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_2}` as const
const FIELD_ACC_BUSINESS_POSTALCODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_postalcode}` as const
const FIELD_ACC_DOMAIN = `${ALIAS_ACCOUNTS}${AccountsModelFields.domain}` as const

const FIELD_PS_TYPE = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.type}` as const
const FIELD_PS_COLOUR = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.colour}` as const
const FIELD_PS_SEQUENCE = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.sequence}` as const
const FIELD_PS_DECISION_STATUS = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.decision_status}` as const

const FIELD_FUND_ID = `${ALIAS_FUNDS}${FundsModelFields.id}` as const
const FIELD_FUND_NAME = `${ALIAS_FUNDS}${FundsModelFields.name}` as const

// Agent Access 4.5: null unless an agent session created this record.
const PROG_CREATED_PROVENANCE = `${FIELD_KEY_PROG}created_provenance` as const

export const ProspectRecordSchema = z
  .object({
    [FIELD_PROSPECT_ID]: z.string().nullish(),
    [FIELD_PROSPECT_INVESTOR]: z.string().nullish(),
    [FIELD_PROSPECT_FUND]: z.string().nullish(),
    [FIELD_PROSPECT_CURRENCY]: z.string().nullish(),
    [FIELD_PROSPECT_COMMITMENT]: z.number().nullish(),
    [FIELD_PROSPECT_ALLOCATION_AVAILABLE]: z.number().nullish(),
    [FIELD_PROSPECT_PIPELINE_ORIGINATION_ID]: z.string().nullish(),
    [FIELD_PROSPECT_PIPELINE_EXECUTION_ID]: z.string().nullish(),
    [FIELD_PROSPECT_NOTES]: z.string().nullish(),
    [FIELD_PIPELINE_PROSPECTS_STAGE]: z.string().nullish(),
    [FIELD_PIPELINE_PROSPECTS_STAGE_DATE]: z.string().nullish(),
    [FIELD_PIPELINE_PROSPECTS_SEQUENCE]: z.number().nullish(),
    [FIELD_PIPELINE_PROSPECTS_ID]: z.string().nullish(),
    [FIELD_PIPELINE_CONFIG_ID]: z.string().nullish(),

    [FIELD_ACC_ID]: z.string().nullish(),
    [FIELD_ACC_NAME]: z.string().nullish(),
    [FIELD_ACC_DESCRIPTION]: z.string().nullish(),
    [FIELD_ACC_NOTES]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_BUSINESS_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_1]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_2]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_POSTALCODE]: z.string().nullish(),
    [FIELD_ACC_DOMAIN]: z.string().nullish(),

    [FIELD_PS_TYPE]: z.string().nullish(),
    [FIELD_PS_COLOUR]: z.string().nullish(),
    [FIELD_PS_SEQUENCE]: z.number().nullish(),
    [FIELD_PS_DECISION_STATUS]: z.string().nullish(),

    [FIELD_FUND_ID]: z.string().nullish(),
    [FIELD_FUND_NAME]: z.string().nullish(),

    [`${FIELD_ACC_BUSINESS_CITY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ACC_BUSINESS_COUNTRY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PS_DECISION_STATUS}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PS_TYPE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PROSPECT_CURRENCY}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_PROSPECTS_STAGE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_PROSPECTS_STAGE_DATE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_PROSPECTS_SEQUENCE}${FIELD_KEY_LABEL}` as const]: z.number().nullish(),
    [`${FIELD_PIPELINE_CONFIG_ID}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),

    [PROG_IS_NEW_INVESTOR]: z.boolean().nullish(),
    [PROG_COMPANY_TEAM]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_TASKS]: z.array(TaskRecordSchema).nullish(),
    [PROG_ACCOUNT_INTERACTION_DATES]: z
      .object({
        first_meeting_date: z.string().nullish(),
        first_meeting: z.string().nullish(),
        last_meeting_date: z.string().nullish(),
        last_meeting: z.string().nullish(),
        last_email_date: z.string().nullish(),
        last_email: z.string().nullish(),
        last_interaction_date: z.string().nullish(),
        last_interaction: z.string().nullish(),
        next_meeting_date: z.string().nullish(),
        next_meeting: z.string().nullish()
      })
      .nullish(),
    [FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [FIELD_CONTACT_ACCOUNTS_SOURCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [FIELD_CONTACT_PROSPECTS_LEAD]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [PROG_CREATED_PROVENANCE]: AgentProvenanceEntrySchema.nullish()
  })
  .passthrough()

export const ProspectRecordFields = makeFieldKeys(ProspectRecordSchema)

export type ProspectRecord = z.infer<typeof ProspectRecordSchema>

export function validateProspectRecord(
  data: unknown,
  options?: { isArray?: boolean }
): ProspectRecord | ProspectRecord[] {
  try {
    return validateWithZodSchema(ProspectRecordSchema, data, 'prospect record', options) as
      | ProspectRecord
      | ProspectRecord[]
  } catch (error) {
    throw new Error(`Prospect record validation failed: ${error}`)
  }
}
